import * as React from 'react';

import { useAtom } from 'jotai';

import { languageAtom } from '~shared/atoms';
import { Stack } from '~shared/components/Stack';
import { ReactNodeNoStrings } from '~shared/types/ReactNodeNoStrings';
import { ResultsSettings } from '~teacher/components/ResultsHeader/ResultsSettings';
import { ActivitySettingsCamelCase } from '~teacher/models/activities';

interface Props {
  children?: ReactNodeNoStrings;
  title: string;
  startDate: string;
  endDate?: string;
  standard?: string;
  activitySettings: ActivitySettingsCamelCase;
}

/**
 * Displays title text, optional date label, and action buttons. Used
 * on Activity Reports or Results, or live results.
 */
export function ResultsHeader({
  children,
  title,
  startDate,
  endDate,
  standard,
  activitySettings,
}: Props): React.ReactElement {
  const [language] = useAtom(languageAtom);

  return (
    <header className="flex flex-col items-start gap-2 lg:flex-row lg:items-start lg:justify-between">
      <Stack>
        <div className="flex items-center">
          <h1
            className="pt-8 m-0 font-display text-lg font-700 leading-12 md:leading-16 text-blue-900 capsize"
            style={{ overflowWrap: 'anywhere' }}
          >
            {title}
            <div className="inline-block">
              <ResultsSettings
                settings={activitySettings}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </h1>
        </div>

        <div className="flex m-0 text-sm font-400 leading-10 text-blue-600 capsize">
          {standard ? standard : null}
          {startDate && endDate ? (
            <time className={standard ? 'ml-6' : ''} dateTime={endDate}>
              {new Intl.DateTimeFormat(language, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hourCycle: 'h12', // 12-hour time for chrome.
              } as any).format(new Date(endDate))}
            </time>
          ) : null}
        </div>
      </Stack>
      <div className="flex flex-wrap gap-2 justify-start shrink-0 w-full py-2 md:justify-end md:w-auto md:py-0">
        {children}
      </div>
    </header>
  );
}
